import * as THREE from 'three'
import {
  ImportedModelObjectMetadataService,
  type ImportedModelObjectHierarchyMetadata,
} from '../../imported-model/metadata'
import { isFeatureResultMesh } from '../../mesh/feature'

/** imported-model 场景挂载使用的内部容器名称。 */
export const IMPORTED_MODEL_SCENE_WRAPPER_NAME = 'imported-assembly'

/**
 * 读取源对象的 imported-model 层级语义。
 *
 * @param object 源 Object3D。
 * @returns 当前对象自身的权威导入层级；不存在时返回 undefined。
 */
export function readVisibleExportSourceHierarchy(
  object: THREE.Object3D,
): ImportedModelObjectHierarchyMetadata | undefined {
  return ImportedModelObjectMetadataService.peekMetadata(object)?.hierarchy
}

/**
 * 判断 Object3D 局部变换是否为单位变换。
 *
 * @param object 源 Object3D。
 * @returns 是否没有局部平移、旋转和缩放。
 */
function hasIdentityLocalTransform(object: THREE.Object3D): boolean {
  return object.position.lengthSq() === 0
    && object.quaternion.x === 0
    && object.quaternion.y === 0
    && object.quaternion.z === 0
    && object.quaternion.w === 1
    && object.scale.x === 1
    && object.scale.y === 1
    && object.scale.z === 1
}

/**
 * 读取 imported runtime 对象的稳定装配身份。
 *
 * @param object 场景对象。
 * @returns assemblyNodeId；不存在时返回空字符串。
 */
export function resolveVisibleExportSourceAssemblyNodeId(object: THREE.Object3D): string {
  const metadata = ImportedModelObjectMetadataService.peekMetadata(object)
  return metadata?.selectionTarget?.assemblyNodeId?.trim()
    || metadata?.assemblyNodeId?.trim()
    || metadata?.hierarchy?.meta?.assemblyNodeId?.trim()
    || ''
}

/**
 * 判断对象是否为 imported-model 加载链创建的非语义运行时包装层。
 *
 * 显式 `runtimeWrapper` 标记是导入边界的权威判定；历史运行时还会创建无 metadata 的
 * `imported-assembly` 容器，因此仅在它同时缺少 hierarchy 与稳定装配身份时兼容识别。
 * 真正的业务 occurrence 即使显示名相同，只要携带稳定身份就不会被这里误折叠。
 *
 * @param object 待判断的场景对象。
 * @returns 是否为只负责承载子树与局部矩阵的非语义包装层。
 */
function isImportedRuntimeWrapperLayer(object: THREE.Object3D): boolean {
  const metadata = ImportedModelObjectMetadataService.peekMetadata(object)
  if (metadata?.runtimeWrapper === true) {
    return true
  }

  return object.name === IMPORTED_MODEL_SCENE_WRAPPER_NAME
    && !readVisibleExportSourceHierarchy(object)
    && resolveVisibleExportSourceAssemblyNodeId(object).length === 0
}

/**
 * 判断根层对象是否只是 GLTFLoader 为单个 display mesh 建立的刚体承载层。
 *
 * 父子共享同一 assemblyNodeId 时，Group 不代表新的业务 occurrence。折叠它并把局部矩阵
 * 乘到唯一子 mesh，可恢复首次导出时“装配节点与 display mesh 同节点”的规范 GLB 结构。
 *
 * @param object 根层 imported runtime 对象。
 * @returns 是否应折叠该承载层。
 */
function shouldFlattenRootImportedDisplayCarrier(object: THREE.Object3D): boolean {
  if (object instanceof THREE.Mesh || object.children.length !== 1) {
    return false
  }
  const child = object.children[0]
  if (!(child instanceof THREE.Mesh)) {
    return false
  }
  const ownerAssemblyNodeId = resolveVisibleExportSourceAssemblyNodeId(object)
  const childAssemblyNodeId = resolveVisibleExportSourceAssemblyNodeId(child)
  return Boolean(ownerAssemblyNodeId && ownerAssemblyNodeId === childAssemblyNodeId)
}

/**
 * 判断对象是否是 imported-model 场景挂载层的内部容器。
 *
 * @param object 待判断对象。
 * @returns 是否应在离线包装配结构中跳过该容器。
 */
export function isImportedModelSceneWrapper(object: THREE.Object3D): boolean {
  return hasIdentityLocalTransform(object)
    && isImportedRuntimeWrapperLayer(object)
}

/**
 * 递归展开 imported-model 加载链创建的运行时包装层。
 *
 * GLTFLoader 可能产生 `scene -> document root -> business root` 多层容器链；导入器会将
 * 连续的无语义容器显式标记为 runtime wrapper，这里统一展开到首个业务节点。
 *
 * @param sources 当前导出入口候选对象。
 * @returns 不包含 imported runtime wrapper 的业务根集合。
 */
export function unwrapImportedModelSceneWrappers(
  sources: readonly THREE.Object3D[],
): THREE.Object3D[] {
  return sources.flatMap((source) => (
    isImportedModelSceneWrapper(source)
      ? unwrapImportedModelSceneWrappers(source.children)
      : [source]
  ))
}

/**
 * 解析可见导出的真实业务根集合。
 *
 * Viewer 场景根自身可能已经是业务 Mesh、特征结果或 imported hierarchy 节点；普通场景根
 * 则从 children 开始，并统一递归剔除 GLTFLoader 创建的 runtime wrapper 链。
 *
 * @param sceneRoot 当前 Viewer 场景根。
 * @returns 不包含 imported runtime wrapper 的业务根集合。
 */
export function resolveVisibleExportSourceRoots(sceneRoot: THREE.Object3D): THREE.Object3D[] {
  const sourceRoots = sceneRoot instanceof THREE.Mesh
    || Boolean(readVisibleExportSourceHierarchy(sceneRoot))
    || isFeatureResultMesh(sceneRoot)
    ? [sceneRoot]
    : sceneRoot.children
  return unwrapImportedModelSceneWrappers(sourceRoots)
}

/**
 * 判断是否折叠非业务包装层。名称是展示信息，不能证明两个节点属于同一个 occurrence。
 * 显式包装层、与父节点共享身份的运行时镜像，以及根层单显示载体可以折叠；其他节点保留。
 * @param input 当前节点及已经建立的父装配身份。
 * @returns 是否将该节点的矩阵折叠到子节点。
 */
export function shouldFlattenVisibleExportAssemblyLayer(input: {
  /** 当前源对象。 */
  object: THREE.Object3D
  /** 已建立的父装配身份；根节点为 null。 */
  parentAssemblyNodeId: string | null
}): boolean {
  if (input.object instanceof THREE.Mesh) return false
  if (isImportedRuntimeWrapperLayer(input.object)) return true

  const currentAssemblyNodeId = resolveVisibleExportSourceAssemblyNodeId(input.object)
  if (input.parentAssemblyNodeId !== null) {
    // 即使名称相同、节点暂时为空或带有直接几何，只有身份一致才能认定为父节点镜像。
    return Boolean(currentAssemblyNodeId && currentAssemblyNodeId === input.parentAssemblyNodeId)
  }
  return shouldFlattenRootImportedDisplayCarrier(input.object)
}

/**
 * 判断显示 Mesh 是否归属父装配节点。多材质 primitive 可名称不同，但必须共用所属 occurrence。
 * 有独立稳定 ID 的 Mesh 不因同名被并入另一节点；没有身份时只消费显式 primitive/包装语义。
 * @param input 当前 Mesh 及父装配身份。
 * @returns 是否复用父节点承载 display/body 绑定。
 */
export function shouldBindVisibleExportMeshToParentAssemblyNode(input: {
  /** 当前显示源 Mesh。 */
  object: THREE.Mesh
  /** 已建立的父装配身份；根节点为 null。 */
  parentAssemblyNodeId: string | null
}): boolean {
  if (input.parentAssemblyNodeId === null) return false
  const currentAssemblyNodeId = resolveVisibleExportSourceAssemblyNodeId(input.object)
  if (currentAssemblyNodeId) return currentAssemblyNodeId === input.parentAssemblyNodeId

  const metadata = ImportedModelObjectMetadataService.peekMetadata(input.object)
  return metadata?.hierarchy?.meta?.semanticKind === 'primitive' || metadata?.runtimeWrapper === true
}
